import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useFetcher,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "@remix-run/react";
import { recalculateOrder, resLineToCart } from "~/helpers/cartHelpers";
import SiteContext from "./SiteContext";
import {
  CartLine,
  DeliveryInfo,
  applyOrderTypeChange,
  deliveryAddressKey,
  isOrderEditable,
  OrderFulfillmentType,
  OrderSource,
  OrderType,
  ResponseOrderWithCartLines,
  ResponseOrderWithLines,
} from "~/entities/orderEntities";
import MenuContext from "./MenuContext";
import { EMPTY_CUSTOMER, EMPTY_ORDER_RESPONSE } from "~/api/endpoints/Order";
import TaxContext from "~/contexts/TaxContext";
import {
  getOrderType,
  getPromotionsRequiringSelection,
  PromotionRequiringSelection,
} from "~/helpers/orderHelper";
import { BaseUser } from "~/entities/authEntities";
import { getRouteEnd, urlGen } from "~/helpers/routesHelpers";
import { KEYS } from "~/modules/checkout/constants";
import PromotionContext from "~/contexts/PromotionContextProvider";
import ChangeOrderModal from "~/modules/order/components/ChangeOrderModal/ChangeOrderModal";
import ChooseTime from "~/modules/order/components/ChooseTime/ChooseTime";
import ChooseTimeSheet from "~/modules/order/components/ChooseTime/ChooseTimeSheet";
import AuthContext from "./AuthContext";
import ConfirmActionModal from "~/modules/order/components/ConfirmActionModal/ConfirmActionModal";
import { getOptionalJSONNullStrFormValue } from "~/helpers/formHelpers";
import { clearOrderId, saveOrderId } from "~/helpers/orderPersistence.client";
import StoreFinder from "~/modules/sites/components/StoreFinder/StoreFinder";
import StoreFinderShell from "~/modules/sites/components/StoreFinder/StoreFinderShell";
import type { OrgSites, SiteListObject } from "~/entities/siteEntities";
import {
  findOrderTypeForFulfilment,
  fulfilmentFromOrderType,
} from "~/helpers/storeFinderFulfilment";
import type { DeliveryReasonCode } from "~/helpers/deliveryCheck";
import DeliveryUnavailableModal from "~/modules/order/components/DeliveryUnavailableModal/DeliveryUnavailableModal";

const OrderContext = createContext<{
  orderSummary: ResponseOrderWithCartLines;
  orderSource: OrderSource;
  orderTypeCode: string;
  orderType?: OrderType;
  deliveryInfo: DeliveryInfo | null;
  isTableOrdering: boolean;
  setTableNumber: (value: string) => void;
  getOrderLineById: (id: string) => CartLine | null | undefined;
  addOrderLine: (line: CartLine) => void;
  removeOrderLine: (id: string) => void;
  updateOrderLine: (line: CartLine) => void;
  reset: () => void;
  refresh: () => void;
  setCustomerData: (x: BaseUser, allowSavePersonalData?: boolean) => void;
  redeemPromotion: (key: string) => void;
  removePromotion: () => void;
  redeemPromotionCode: (code: string) => void;
  removePromotionCode: () => void;
  /**
   * Drop a flash offer's reward line AND its promo code in one submit.
   *
   * Calling removeOrderLine then removePromotionCode does not work: both build
   * their payload from the same `orderSummary` snapshot, so the second submit
   * carries the line the first just removed and puts it straight back.
   */
  removeFlashOffer: (lineId: string) => void;
  setOrderTypeInfo: (
    orderType: OrderType,
    reqTime: string | null,
    deliveryInfo: DeliveryInfo | null,
    resetOrder: boolean
  ) => void;
  numTimeSlots: number;
  successPageOrderInfo: ResponseOrderWithLines | null;
  openOrderTimeModal: VoidFunction;
  openTimeSheet: VoidFunction;
  openStoreFinder: (initialFulfilment?: "PICKUP" | "DELIVERY") => void;
  promotionsRequiringSelection: PromotionRequiringSelection[];
  setGroupOrder: (val: boolean, redirectToPayment: boolean) => void;
  orderLoading: boolean;
  setConfirmingAction: (x: ConfirmingAction | null) => void;
  readonlyCart: boolean;
  asapWait: number | null;
  signBackInRequired: boolean;
  sessionRequestedTime: string | null | undefined;
}>({
  orderSummary: EMPTY_ORDER_RESPONSE,
  orderSource: OrderSource.ONLINE,
  orderTypeCode: "",
  deliveryInfo: null,
  isTableOrdering: false,
  setTableNumber: () => null,
  getOrderLineById: () => null,
  addOrderLine: () => null,
  removeOrderLine: () => null,
  updateOrderLine: () => null,
  reset: () => null,
  refresh: () => null,
  setCustomerData: () => null,
  redeemPromotion: () => null,
  removePromotion: () => null,
  redeemPromotionCode: () => null,
  removePromotionCode: () => null,
  removeFlashOffer: () => null,
  setOrderTypeInfo: () => null,
  numTimeSlots: 0,
  successPageOrderInfo: null,
  openOrderTimeModal: () => null,
  openTimeSheet: () => null,
  openStoreFinder: () => null,
  promotionsRequiringSelection: [],
  setGroupOrder: () => null,
  orderLoading: true,
  setConfirmingAction: () => null,
  readonlyCart: true,
  asapWait: null,
  signBackInRequired: false,
  sessionRequestedTime: null,
});

export interface ConfirmingAction {
  action: () => any;
  icon: () => JSX.Element;
  title: string;
  body: string;
}

export const OrderContextProvider: React.FC<
  PropsWithChildren<{
    orderInfo: ResponseOrderWithLines | null;
    clearOrder: boolean;
    orderSource: OrderSource;
    orderTypeCode: string;
    deliveryInfo: DeliveryInfo | null;
    paramOrderSource: string;
    paramMenuCode?: string;
    availableTimeSlots: (string | null)[];
    requestedReadyTime?: string | null;
    tableNumber?: string | null;
    savedCustomer?: BaseUser;
    timeValid: boolean;
    asapWait: number | null;
    signBackInRequired: boolean;
    sessionRequestedTime: string | null | undefined;
    asapAvailable: boolean;
  }>
> = ({
  children,
  savedCustomer,
  orderInfo: unclearedOrderInfo,
  clearOrder,
  orderSource,
  orderTypeCode: loaderOrderTypeCode,
  deliveryInfo: loaderDeliveryInfo,
  paramOrderSource,
  paramMenuCode,
  availableTimeSlots,
  requestedReadyTime,
  tableNumber,
  timeValid,
  asapWait,
  signBackInRequired,
  sessionRequestedTime,
  asapAvailable,
}) => {
  const { scheduledPromotions } = useContext(PromotionContext);
  const [showOrderTimeModal, setShowOrderTimeModal] = useState(false);
  const [showTimeSheet, setShowTimeSheet] = useState(false);
  // Task 9: unified multi-site StoreFinder surface (opened from StoreHeader).
  const [showStoreFinder, setShowStoreFinder] = useState(false);
  const [confirmingAction, setConfirmingAction] =
    useState<ConfirmingAction | null>(null);
  const { site, orgId } = useContext(SiteContext);
  const { taxFromCode } = useContext(TaxContext);
  const { menu } = useContext(MenuContext);
  const { useComoAuth } = useContext(AuthContext);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useFetcher({ key: KEYS.SUBMIT_ORDER_FETCHER });
  // Task 9: lazy loader for the org's site list, used only by the StoreFinder.
  // This keeps the full site list off every page load — it is fetched the
  // first time the finder is opened from the StoreHeader.
  const orgSitesFetcher = useFetcher<OrgSites>();

  const { table: paramTableNumber } = useParams();

  const isTableOrdering = orderSource === OrderSource.TABLE_ORDERING;
  const defaultTableNumber = paramTableNumber ?? tableNumber;

  // Get the order type code from the fetcher request data, but only for requests where it is specified
  // (i.e. requests to change order type)
  const orderTypeCode = useMemo(() => {
    if (fetcher.state === "idle") {
      // When fetcher state is idle however, force getting the value from the loader, to prevent issues where the session is
      // not getting set correctly from being hidden / harder to reproduce.
      return loaderOrderTypeCode;
    } else {
      return (
        fetcher.formData?.get("orderTypeCode")?.toString() ??
        loaderOrderTypeCode
      );
    }
  }, [loaderOrderTypeCode, fetcher.state, fetcher.formData]);

  const orderSummary: ResponseOrderWithCartLines = useMemo(() => {
    let useLoaderOrderInfo = false;
    let actualClearOrder = clearOrder;
    let currentlySettingRequestedTime: string | null | undefined;

    if (fetcher.state === "idle") {
      // Loader data is up to date - get data from that
      useLoaderOrderInfo = true;
    } else if (fetcher.formData?.get("isReset")) {
      // If currently resetting the order, override clearOrder to true
      useLoaderOrderInfo = true;
      actualClearOrder = true;
      // Get requested time currently being set (when being set), so changes to requested ready time can optimistically update
      currentlySettingRequestedTime =
        fetcher.formData &&
        getOptionalJSONNullStrFormValue(fetcher.formData, "setRequestedTime");
    }

    if (useLoaderOrderInfo) {
      if (unclearedOrderInfo && !actualClearOrder) {
        if (!menu?.tabs) {
          // Hide lines when no menu data (as can't map to cart structure)
          console.warn("Menu or menu tabs not available");
          return { ...unclearedOrderInfo, lines: [] };
        }

        // Get order from api response
        return {
          ...unclearedOrderInfo,
          lines: unclearedOrderInfo.lines
            .map((line) => resLineToCart(line, menu))
            .filter((x) => !!x),
        };
      }

      // Generate empty order
      return {
        ...EMPTY_ORDER_RESPONSE,
        orderTypeCode,
        ...(isTableOrdering && { tableNumber: defaultTableNumber }),
        customer: savedCustomer ?? EMPTY_CUSTOMER, // initialise customer from session data only for creating initial order
        requestedReadyTime:
          currentlySettingRequestedTime !== undefined
            ? currentlySettingRequestedTime
            : (requestedReadyTime ?? null),
        deliveryInfo: loaderDeliveryInfo,
        lines: [],
        groupOrder: isTableOrdering,
      };
    }

    // Action submitted but loaders not loaded yet
    // Get data from action response
    if (fetcher.state === "loading") {
      const data: any = fetcher.data;
      if (data?.resOrder) {
        const orderInfo: ResponseOrderWithLines = data.resOrder;
        if (!menu?.tabs) {
          // Hide lines when no menu data (as can't map to cart structure)
          console.warn("Menu or menu tabs not available");
          return { ...orderInfo, lines: [] };
        }
        return {
          ...data.resOrder,
          lines: orderInfo.lines
            .map((line) => resLineToCart(line, menu))
            .filter((x) => !!x),
        };
      }
    }

    // Action submitted and currently processing
    // Get order from action request
    const formValue: ResponseOrderWithCartLines = JSON.parse(
      fetcher.formData?.get("orderSummary")?.toString() ?? ""
    );
    return formValue;
  }, [
    menu,
    fetcher.state,
    fetcher.formData,
    fetcher.data,
    unclearedOrderInfo,
    isTableOrdering,
    defaultTableNumber,
    savedCustomer,
    requestedReadyTime,
    loaderDeliveryInfo,
    orderTypeCode,
    clearOrder,
  ]);

  // Cart readonly when time is not valid, the order is already placed (not a
  // DRAFT — updating it 400s), there are pending payments, or we are currently
  // resetting or inserting a new order.
  const pendingPayments = !!orderSummary?.pendingPaymentIds?.length;
  const orderPlaced = !isOrderEditable(orderSummary?.status);
  const readonlyCart = useMemo(
    () =>
      !timeValid ||
      orderPlaced ||
      pendingPayments ||
      (fetcher.state === "submitting" &&
        (!!fetcher.formData?.get("isReset") ||
          !!fetcher.formData?.get("forceInsert"))),
    [fetcher.state, fetcher.formData, timeValid, orderPlaced, pendingPayments]
  );

  // Helper function to submit order actions
  const submitOrderAction = ({
    order,
    preventInsert,
    allowSavePersonalData,
    setOrderTypeCode,
    setRequestedTime,
    setDeliveryInfo,
    redirectToPayment,
    errorOnInvalidAsset,
  }: {
    order: ResponseOrderWithCartLines | null;
    preventInsert?: boolean;
    allowSavePersonalData?: boolean;
    setOrderTypeCode?: string;
    setRequestedTime?: string | null;
    setDeliveryInfo?: DeliveryInfo | null;
    redirectToPayment?: boolean;
    errorOnInvalidAsset?: boolean;
  }) => {
    const formData = new FormData();

    let clearCache = false;

    formData.append("orgId", orgId);

    if (errorOnInvalidAsset) {
      formData.append("errorOnInvalidAsset", "true");
    }

    // check for undefined, as null (ASAP) is a valid time to set
    if (setRequestedTime !== undefined) {
      clearCache = true;
      formData.append("setRequestedTime", JSON.stringify(setRequestedTime));
    }

    if (setOrderTypeCode) {
      clearCache = true;
      formData.append("setOrderTypeCode", setOrderTypeCode);
    }

    // Check for undefined, not truthiness: `null` is the value used to CLEAR a
    // delivery address (e.g. Delivery→Pickup). A truthy guard dropped `null`, so
    // the address was never cleared and the reprice sync re-attached the stale
    // one onto a pickup order (wrong total risk). The route action already keys
    // on `!== undefined` (session.set("deliveryInfo", …)), mirroring setRequestedTime.
    if (setDeliveryInfo !== undefined) {
      clearCache = true;
      formData.append("setDeliveryInfo", JSON.stringify(setDeliveryInfo));
    }

    if (preventInsert) {
      formData.append("preventInsert", "true");
    }

    // If the context order has no id, likely we have a new order, or the order in session is no longer valid (e.g. it is already sent)
    if (!order?.id) {
      if (preventInsert) {
        // Insert is prevented, but the order is not allowed to be updated, so we must also prevent update
        formData.append("preventUpdate", "true");
      } else {
        // Force insert a new order since we are not preventing insert
        clearCache = true;
        formData.append("forceInsert", "true");
      }
    }

    if (redirectToPayment) {
      formData.append("redirectToPayment", "true");
    }

    if (order) {
      const orderSummary = recalculateOrder(
        order,
        taxFromCode,
        scheduledPromotions
      );

      formData.append("orderSummary", JSON.stringify(orderSummary));
    } else {
      clearCache = true;
      formData.append("isReset", "true");

      if (useComoAuth) {
        localStorage.setItem("last-reset", new Date().toISOString());
      }
    }

    if (allowSavePersonalData != undefined) {
      formData.append(
        "allowSavePersonalData",
        allowSavePersonalData.toString()
      );
    }

    fetcher.submit(formData, {
      action: `/${paramOrderSource}/${site.id}${
        paramTableNumber ? "/" + paramTableNumber : ""
      }?clearCache=${clearCache}`,
      method: "post",
    });
  };

  const setTableNumber = (value: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: { ...orderSummary, tableNumber: value },
    });
  };

  const setTableRef = useRef(setTableNumber);
  setTableRef.current = setTableNumber;
  useEffect(() => {
    if (paramTableNumber && orderSummary.tableNumber !== paramTableNumber) {
      setTableRef.current(paramTableNumber);
    }
  }, [orderSummary.tableNumber, paramTableNumber]);

  const getOrderLineById = (id: string) => {
    return orderSummary.lines.find((orderLine) => orderLine.id === id) || null;
  };

  const addOrderLine = (orderLine: CartLine) => {
    if (readonlyCart) {
      return;
    }

    if (
      !orderLine.item.product.children.length &&
      !orderLine.item.children.length
    ) {
      const combineWith = orderSummary.lines.find(
        (x) =>
          x.item.productCode == orderLine.item.productCode &&
          !x.item.children.length
      );
      if (combineWith) {
        updateOrderLine({
          ...combineWith,
          item: {
            ...combineWith.item,
            qty: combineWith.item.qty + orderLine.item.qty,
          },
        });
        return;
      }
    }

    const newOrderLines = [...orderSummary.lines, orderLine];
    submitOrderAction({ order: { ...orderSummary, lines: newOrderLines } });
  };

  const removeOrderLine = (id: string) => {
    if (readonlyCart) {
      return;
    }
    const newOrderLines = orderSummary.lines.filter(
      (orderLine) => orderLine.id !== id
    );
    submitOrderAction({ order: { ...orderSummary, lines: newOrderLines } });
  };

  const updateOrderLine = (updatedOrderLine: CartLine) => {
    if (readonlyCart) {
      return;
    }
    const newOrderLines = orderSummary.lines.map((orderLine) =>
      orderLine.id === updatedOrderLine.id ? updatedOrderLine : orderLine
    );

    submitOrderAction({
      order: { ...orderSummary, lines: newOrderLines },
    });
  };

  const redeemPromotion = (dealKey: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: {
        ...orderSummary,
        redemptionKey: dealKey,
      },
      errorOnInvalidAsset: true,
    });
  };

  const redeemPromotionCode = (code: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: {
        ...orderSummary,
        promoCode: code.trim() === "" ? null : code.trim(),
      },
      errorOnInvalidAsset: true,
    });
  };

  const removePromotion = () => {
    submitOrderAction({
      order: { ...orderSummary, redemptionKey: null },
    });
  };

  const removePromotionCode = () => {
    submitOrderAction({
      order: { ...orderSummary, promoCode: null },
    });
  };

  // One submit, both changes — see the interface comment for why two calls
  // cannot work here
  const removeFlashOffer = (lineId: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: {
        ...orderSummary,
        lines: orderSummary.lines.filter((line) => line.id !== lineId),
        promoCode: null,
      },
    });
  };

  const setCustomerData = (
    customer: BaseUser,
    allowSavePersonalData?: boolean
  ) => {
    submitOrderAction({
      order: { ...orderSummary, customer },
      allowSavePersonalData,
    });
  };

  const setOrderTypeInfo = (
    newOrderType: OrderType,
    requestedTime: string | null,
    deliveryInfo: DeliveryInfo | null,
    isReset: boolean
  ) => {
    submitOrderAction({
      // Only set order type if it is new
      setOrderTypeCode:
        newOrderType.code === orderTypeCode ? undefined : newOrderType.code,
      setRequestedTime: requestedTime,
      setDeliveryInfo: deliveryInfo,
      order: isReset
        ? null
        : applyOrderTypeChange(
            orderSummary,
            newOrderType.code,
            requestedTime,
            deliveryInfo
          ),
      // If no order exists, no need to insert one yet, as the requested time is stored in session
      preventInsert: true,
    });
  };

  const reset = () => {
    submitOrderAction({
      order: null,
    });
  };

  const refresh = () => {
    submitOrderAction({
      order: orderSummary,
    });
  };

  const setGroupOrder = (groupOrder: boolean, redirectToPayment: boolean) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: { ...orderSummary, groupOrder },
      redirectToPayment,
    });
  };

  const promotionsRequiringSelection = useMemo(
    () => getPromotionsRequiringSelection(orderSummary),
    [orderSummary]
  );

  const orderType = useMemo(
    () => getOrderType(orderTypeCode, site.orderTypes),
    [orderTypeCode, site.orderTypes]
  );

  // If order is not attached to the authenticated user, detect this and attach it. Only attempt this once however.
  const { authenticatedUser } = useContext(AuthContext);
  const orderRefreshAttempted = useRef(false);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const orderNeedsRefresh =
    !clearOrder &&
    unclearedOrderInfo?.customerId == null &&
    !!authenticatedUser?.id &&
    fetcher.state === "idle";
  useEffect(() => {
    if (!orderRefreshAttempted.current && orderNeedsRefresh) {
      orderRefreshAttempted.current = true;
      console.warn("Order missing customer id. Refreshing...");
      refreshRef.current();
    }
  }, [orderNeedsRefresh]);

  // ── Re-price when the loader/session order type or delivery info is stale ──
  // The /sites delivery entry navigates with query params; the loader writes
  // deliveryInfo / orderTypeCode to the __order session but does NOT re-price
  // the EXISTING order, so the cart/checkout total omits the delivery fee until
  // the next cart mutation. When an order already exists and its applied state
  // differs from the loader/session, fire ONE re-price.
  //
  // We deliberately do nothing when there is no order: the first add prices
  // correctly, and setOrderTypeInfo with preventInsert would not create one.
  const setOrderTypeInfoRef = useRef(setOrderTypeInfo);
  setOrderTypeInfoRef.current = setOrderTypeInfo;
  // Guard keyed on orderId + applied-vs-target signature so the re-price fires
  // exactly once per distinct change. After it lands, orderSummary matches the
  // loader values and the key stops changing, so it never loops.
  const repricedKeyRef = useRef<string | null>(null);

  const orderId = orderSummary?.id;
  // Key on the delivery address only — backend-managed fields (uberDirect quote,
  // instructions) that the session never carries would otherwise make these
  // perpetually unequal and keep re-firing the sync (crashing on a SENT order).
  const loaderDeliveryInfoKey = deliveryAddressKey(loaderDeliveryInfo);
  const appliedDeliveryInfoKey = deliveryAddressKey(
    orderSummary?.deliveryInfo ?? null
  );
  const repriceNeeded =
    fetcher.state === "idle" &&
    !!orderId &&
    !clearOrder &&
    // Never re-price a placed order — the update PUT would 400 (INVALID_ORDER_STATUS)
    // and crash the success page. Only DRAFT orders are editable.
    isOrderEditable(orderSummary?.status) &&
    (orderSummary.orderTypeCode !== orderTypeCode ||
      appliedDeliveryInfoKey !== loaderDeliveryInfoKey);
  // Target order type for the new fulfilment, resolved from the current site.
  const repriceOrderType = orderType;

  useEffect(() => {
    if (!repriceNeeded || !repriceOrderType) return;

    const repriceKey = `${orderId}|${orderTypeCode}|${loaderDeliveryInfoKey}`;
    if (repricedKeyRef.current === repriceKey) return;
    repricedKeyRef.current = repriceKey;

    setOrderTypeInfoRef.current(
      repriceOrderType,
      orderSummary.requestedReadyTime ?? null,
      loaderDeliveryInfo,
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    repriceNeeded,
    repriceOrderType,
    orderId,
    orderTypeCode,
    loaderDeliveryInfoKey,
  ]);

  // ── "Store doesn't deliver here" graceful handling ─────────────────────
  // The menu action returns { deliveryUnavailable, deliveryReason } instead of
  // throwing when addOrder/updateOrder rejects with a delivery error type. We
  // surface a calm modal here rather than the route ErrorBoundary. The fetcher
  // data persists after the modal is dismissed, so we track dismissal keyed on
  // the response identity to avoid re-opening it on unrelated re-renders.
  const deliveryUnavailable = !!(fetcher.data as any)?.deliveryUnavailable;
  const deliveryReason: DeliveryReasonCode | null =
    ((fetcher.data as any)?.deliveryReason as DeliveryReasonCode) ?? null;
  const [dismissedDeliveryData, setDismissedDeliveryData] = useState<
    unknown | null
  >(null);
  const showDeliveryUnavailable =
    deliveryUnavailable && dismissedDeliveryData !== fetcher.data;

  // Resolve a pickup order type to switch to (COLLECTION preferred, IN_STORE as
  // a fallback), scoped to the current order source / site.
  const pickupOrderType = useMemo(() => {
    const collection = findOrderTypeForFulfilment(
      site.orderTypes,
      "PICKUP",
      orderSource
    );
    if (collection) return collection;
    return site.orderTypes.find(
      (o) =>
        o.active &&
        o.fulfillmentType === OrderFulfillmentType.IN_STORE &&
        o.source === orderSource
    );
  }, [site.orderTypes, orderSource]);

  const handleSwitchToPickup = () => {
    setDismissedDeliveryData(fetcher.data);
    if (!pickupOrderType) return;
    // Drop the delivery address and re-price as pickup. This goes through the
    // normal order action (preventInsert in setOrderTypeInfo means it only
    // updates an existing order; the next add prices correctly otherwise).
    setOrderTypeInfo(
      pickupOrderType,
      orderSummary.requestedReadyTime ?? null,
      null,
      false
    );
  };

  // Mirror the active order id AND its site to localStorage so it survives
  // cookie drops (in-app browsers, 3-h expiry). orderSummary.id and site.id both
  // come from the same loader snapshot, so the pair is always consistent (no
  // stale-write race). clearOrder means the cart is being invalidated
  // (completed / new site), so we clear the stored entry too.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (clearOrder || !orderSummary.id) {
      clearOrderId();
    } else {
      saveOrderId(orderSummary.id, site.id);
    }
  }, [orderSummary.id, clearOrder, site.id]);

  // Get the user to confirm an order time when asap is not available for the selected order type,
  //   and a requested time has not already been set in the session
  const timeNeedsSelecting =
    !searchParams.has("allowImplicitSettings") &&
    orderSummary.id === "" &&
    sessionRequestedTime == null &&
    !asapAvailable;

  const location = useLocation();
  const pathEnd = getRouteEnd(location.pathname);
  // The legacy ChangeOrderModal used to auto-pop on the menu whenever ASAP
  // wasn't available — which now duplicates the OfflineBanner that already
  // tells the user the service is unavailable. Disable the auto-pop entirely;
  // the StoreHeader segments + ChooseTimeSheet are the change paths. (R1 T1.4
  // in the plan formally retires this trigger.)
  const autoShowOrderTypeModal =
    false &&
    timeNeedsSelecting &&
    !["cancelled", "checkout", "declined", "success", "payment"].includes(
      pathEnd
    );

  // ── Task 9: unified StoreFinder ───────────────────────────────────────
  // Open the finder (from StoreHeader) and lazily load the org's site list
  // the first time it is opened. Optional `initialFulfilment` lets the caller
  // open the finder on a specific tab (e.g. tapping the "Pickup" segment in
  // the StoreHeader opens the finder on Pickup, not whatever the current
  // order is). Cleared on close.
  const [storeFinderFulfilmentOverride, setStoreFinderFulfilmentOverride] =
    useState<"PICKUP" | "DELIVERY" | null>(null);
  const openStoreFinder = (initialFulfilment?: "PICKUP" | "DELIVERY") => {
    if (orgSitesFetcher.state === "idle" && !orgSitesFetcher.data && orgId) {
      orgSitesFetcher.load("/api/orgSites");
    }
    if (initialFulfilment) setStoreFinderFulfilmentOverride(initialFulfilment);
    setShowStoreFinder(true);
  };

  // In catering, only list sites that actually offer catering, so picking a
  // store keeps the customer in the catering experience with a valid order type.
  const allStoreFinderSites: SiteListObject[] =
    orgSitesFetcher.data?.sites ?? [];
  const storeFinderSites: SiteListObject[] =
    orderSource === OrderSource.CATERING
      ? allStoreFinderSites.filter((s) =>
          s.orderTypes?.some(
            (o) => o.active && o.source === OrderSource.CATERING
          )
        )
      : allStoreFinderSites;

  const storeFinderInitialFulfilment =
    storeFinderFulfilmentOverride ?? fulfilmentFromOrderType(orderType);

  // Build a DeliveryInfo from the finder's geocoded address selection, reusing
  // the same DeliveryInfo contract ChangeOrderModal.handleSave produces.
  const deliveryInfoFromFinderAddress = (deliveryAddress?: {
    formattedAddress: string;
    latitude: number;
    longitude: number;
  }): DeliveryInfo | null =>
    deliveryAddress
      ? {
          address: {
            address1: deliveryAddress.formattedAddress,
            address2: "",
            city: "",
            countryId: null,
            postalCode: "",
            state: "",
            latitude: deliveryAddress.latitude,
            longitude: deliveryAddress.longitude,
          },
        }
      : null;

  const handleStoreFinderSelect = ({
    site: chosenSite,
    fulfilment,
    deliveryAddress,
  }: {
    site: SiteListObject;
    fulfilment: "PICKUP" | "DELIVERY";
    deliveryAddress?: {
      formattedAddress: string;
      latitude: number;
      longitude: number;
    };
  }) => {
    // Resolve the chosen fulfilment's active order type *on the chosen site*.
    const chosenOrderType = findOrderTypeForFulfilment(
      chosenSite.orderTypes,
      fulfilment,
      orderSource
    );
    const deliveryInfo = deliveryInfoFromFinderAddress(deliveryAddress);

    if (chosenSite.id !== site.id) {
      // ── Different store ──────────────────────────────────────────────
      // For delivery, persist the chosen address through the existing
      // OrderContext delivery mechanism BEFORE navigating. deliveryInfo lives
      // in the shared (__order) session (not site-scoped), so the destination
      // site's loader reads it back via session.get("deliveryInfo").
      //
      // We persist using the CURRENT orderType (not the destination's) so this
      // write only adds the delivery address to the session and does not mutate
      // the current order's type as the user is leaving this site. The
      // destination order type is carried on the URL (orderTypeCode) instead.
      //
      // NEEDS-VERIFICATION: this relies on the setDeliveryInfo session write
      // (posted to the current-site action) landing before the destination
      // loader runs. Cannot be smoke-tested on bf-demo.
      if (deliveryInfo && orderType) {
        setOrderTypeInfo(
          orderType,
          orderSummary.requestedReadyTime ?? null,
          deliveryInfo,
          false
        );
      }
      // The menu loader re-homes the cart via the Phase-2 changeSite flow when
      // the siteId changes. orderTypeCode is passed so the destination opens on
      // the chosen fulfilment.
      navigate(
        urlGen("Order", {
          orderSource,
          siteId: chosenSite.id,
          tableNumber: paramTableNumber,
          orderTypeCode: chosenOrderType?.code ?? null,
        })
      );
      setShowStoreFinder(false);
      return;
    }

    // ── Same store ───────────────────────────────────────────────────
    setShowStoreFinder(false);

    // Apply the chosen order type immediately. Previously this skipped the
    // update when the order type changed (deferring to ChangeOrderModal's
    // "cart will be lost" confirmation) — but with the modal retired, that
    // guard left the UI stuck (Delivery → Pickup wouldn't update). The menu
    // loader's `shouldClearOrder` still drops cart items when the underlying
    // menu doesn't carry them; re-pricing handles charge changes (e.g. the
    // delivery fee dropping off when switching to Pickup). A proper
    // ConfirmActionModal prompt for cart-loss is Release 1 polish.
    if (chosenOrderType) {
      setOrderTypeInfo(
        chosenOrderType,
        orderSummary.requestedReadyTime ?? null,
        deliveryInfo,
        false
      );
    }

    // Proceed to the TIME step (current site) via the dedicated ChooseTimeSheet
    // rather than the legacy ChangeOrderModal. (Release 1 of the entry-flow
    // rework — the modal is being retired.)
    setShowTimeSheet(true);
  };

  return (
    <OrderContext.Provider
      value={{
        orderSummary,
        orderSource,
        orderTypeCode,
        orderType,
        deliveryInfo: loaderDeliveryInfo,
        isTableOrdering,
        setTableNumber,
        getOrderLineById,
        addOrderLine,
        removeOrderLine,
        updateOrderLine,
        reset,
        refresh,
        setCustomerData,
        redeemPromotion,
        removePromotion,
        redeemPromotionCode,
        removePromotionCode,
        removeFlashOffer,
        setOrderTypeInfo,
        numTimeSlots: availableTimeSlots.length,
        successPageOrderInfo: unclearedOrderInfo,
        openOrderTimeModal: () => setShowOrderTimeModal(true),
        openTimeSheet: () => setShowTimeSheet(true),
        openStoreFinder,
        promotionsRequiringSelection,
        setGroupOrder,
        orderLoading: fetcher.state === "submitting",
        setConfirmingAction,
        readonlyCart,
        asapWait,
        signBackInRequired,
        sessionRequestedTime,
      }}
    >
      {children}
      {/** Change Order modal */}
      <ChangeOrderModal
        visible={showOrderTimeModal || autoShowOrderTypeModal}
        onClose={(isReset, isSave) => {
          if (autoShowOrderTypeModal && !isSave) {
            // Something to hide the modal
            setSearchParams((params) => {
              params.set("allowImplicitSettings", "true");
              return params;
            });
          }

          if (isReset) {
            navigate(
              urlGen("Order", {
                orderSource,
                siteId: site.id,
                menuCode: paramMenuCode,
                tableNumber: paramTableNumber,
              })
            );
          } else if (isSave && searchParams.has("orderTypeCode")) {
            setSearchParams((params) => {
              params.delete("orderTypeCode");
              return params;
            });
          }
          setShowOrderTimeModal(false);
        }}
      />
      {/** "When would you like it?" — direct time picker (opened from StoreHeader's
          ETA chip). Saves through the same setOrderTypeInfo path as the order-type
          modal's time step, so re-pricing/cache behaviour is identical. */}
      <ChooseTimeSheet
        visible={showTimeSheet}
        onClose={() => setShowTimeSheet(false)}
      >
        <ChooseTime
          visible={showTimeSheet}
          availableTimeSlots={availableTimeSlots}
          requestedReadyTime={orderSummary.requestedReadyTime}
          asapWait={asapWait}
          saving={fetcher.state !== "idle"}
          onSave={(value) => {
            if (orderType) {
              // Time-only change: preserve the order's current delivery info.
              setOrderTypeInfo(
                orderType,
                value,
                orderSummary.deliveryInfo ?? loaderDeliveryInfo,
                false
              );
            }
            setShowTimeSheet(false);
          }}
        />
      </ChooseTimeSheet>
      {/** Task 9: unified multi-site StoreFinder (opened from StoreHeader) */}
      <StoreFinderShell
        visible={showStoreFinder}
        onClose={() => {
          setShowStoreFinder(false);
          setStoreFinderFulfilmentOverride(null);
        }}
      >
        {orgSitesFetcher.state === "loading" && !orgSitesFetcher.data ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            Loading stores…
          </div>
        ) : (
          <StoreFinder
            orgId={orgId}
            sites={storeFinderSites}
            prevSiteId={site.id}
            orderSource={orderSource}
            initialFulfilment={storeFinderInitialFulfilment}
            // Pre-fill the Delivery tab + run deliverable lookup when the order
            // already has a delivery address (Sal's "Mobi To-Go" pattern), so
            // the customer doesn't retype their address on every change.
            initialAddress={
              loaderDeliveryInfo?.address?.address1 &&
              loaderDeliveryInfo.address.latitude != null &&
              loaderDeliveryInfo.address.longitude != null
                ? {
                    formattedAddress: loaderDeliveryInfo.address.address1,
                    latitude: loaderDeliveryInfo.address.latitude,
                    longitude: loaderDeliveryInfo.address.longitude,
                  }
                : undefined
            }
            onSelect={handleStoreFinderSelect}
            // TODO: pass regionCodes={org.countryCodes} once the org carries a
            // country (see OrgSites in app/entities/siteEntities.ts). Defaults to NZ.
          />
        )}
      </StoreFinderShell>
      <ConfirmActionModal
        visible={!!confirmingAction}
        onClose={() => setConfirmingAction(null)}
        onConfirm={() => {
          confirmingAction && confirmingAction?.action();
        }}
        icon={confirmingAction?.icon}
        title={confirmingAction?.title}
        body={confirmingAction?.body}
      />
      {/* Calm "store doesn't deliver here" modal (replaces full-page error) */}
      <DeliveryUnavailableModal
        visible={showDeliveryUnavailable}
        reason={deliveryReason}
        canSwitchToPickup={!!pickupOrderType}
        onSwitchToPickup={handleSwitchToPickup}
        onClose={() => setDismissedDeliveryData(fetcher.data)}
      />
    </OrderContext.Provider>
  );
};

export default OrderContext;
